const RETRY_DELAY_MS = 200;

let queue: Promise<void> = Promise.resolve();

function serialized<T>(fn: () => Promise<T>): Promise<T> {
  const task = queue.then(fn);
  queue = task.then(
    () => {},
    () => {},
  );
  return task;
}

function rootUrl(path: string): string {
  return (window.Shopify?.routes?.root ?? "/") + path;
}

async function postJSON(url: string, body: object): Promise<unknown> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
}

interface CartAddItem {
  id: number;
  quantity: number;
  properties?: Record<string, string>;
}

export function cartAdd(items: CartAddItem[]): Promise<unknown> {
  return serialized(() => postJSON(rootUrl("cart/add.js"), { items }));
}

interface CartChangeParams {
  id: string;
  quantity: number;
  properties?: Record<string, string>;
}

export function cartChange(params: CartChangeParams): Promise<unknown> {
  return serialized(() => postJSON(rootUrl("cart/change.js"), params));
}

export function cartUpdate(updates: Record<string, number>): Promise<unknown> {
  return serialized(() => postJSON(rootUrl("cart/update.js"), { updates }));
}

class RateLimitError extends Error {}

function isStringRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

async function fetchSectionOnce(sectionId: string): Promise<string> {
  const response = await fetch(rootUrl(`?sections=${encodeURIComponent(sectionId)}`));
  if (response.status === 429) throw new RateLimitError();
  if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
  const json: unknown = await response.json();
  if (!isStringRecord(json)) {
    throw new Error(`Section ${sectionId} response was not an object`);
  }
  const html = json[sectionId];
  if (typeof html !== "string") throw new Error(`Section ${sectionId} missing from response`);
  return html;
}

/**
 * Fetches rendered section HTML. Retries once on transient errors
 * but not on 429 (retrying under rate limiting makes it worse).
 */
export async function fetchCartSection(sectionId: string): Promise<string> {
  try {
    return await fetchSectionOnce(sectionId);
  } catch (error) {
    if (error instanceof RateLimitError) throw error;
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    return fetchSectionOnce(sectionId);
  }
}
